import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { RemoveScrollBar } from 'react-remove-scroll-bar'
import { RootState } from '../../redux/store'
import PointBar, { POINT_BAR_HORIZONTAL_INSET_CLASS } from '../../components/userAdmin/PointBar'
import CourseTile from '../../components/userAdmin/CourseTile'
import { PageTitle, SkipLink } from '../../components/atoms'
import Footer from '../../components/userAdmin/Footer'
import apiServices from '../../libs/services'
import { Attempt, Module } from '../../models/global.types'
import { useAuth } from '../../hooks/useAuth'
import { useDispatch, useSelector } from 'react-redux'
import { setFirstActiveModule, setModules } from '../../redux/slices/moduleSlice'
import { setCurrentAttempt } from '../../redux/slices/quizSlice'
import config from '../../config/config'
import { ControlHighlight, KeyboardControl } from '../../components/molecules/KeyboardControls'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'
import { useBackgroundMusic } from '../../contexts/BackgroundMusicContext'
import { getBgmUrl, getSfxUrl } from '../../utils/audio'
import { BgmTrack, SfxTrack } from '../../enums/enums'
import {
  DASHBOARD_RESKIN_CHROME_MAX_CLASS,
  DASHBOARD_RESKIN_LIGHTNING_LAUNCH_SRC,
  DASHBOARD_RESKIN_LIGHTNING_SIZE_CLASS,
  DASHBOARD_RESKIN_LIGHTNING_WIDTH_CLASS,
  DASHBOARD_RESKIN_MENU_ICON_SIZE_CLASS,
  DASHBOARD_RESKIN_MENU_ICON_SRC,
  DASHBOARD_RESKIN_MENU_STAGE_CLASS,
  getDashboardContainerBackgroundStyle,
  getDashboardReskinLockedIteration
} from '../../components/userAdmin/dashboardReskinTiles'
import ReskinStudentHeader from '../../components/userAdmin/ReskinStudentHeader'
import { useDashboardReskinFitScale } from '../../components/userAdmin/useDashboardReskinFitScale'
import { useUserPreferences } from '../../contexts/UserPreferencesContext'
import { defaultPageTitle } from '../../components/atoms/PageTitle/PageTitle'
import { logger } from '../../utils'

// Keep these imports as they will used for Redux refactor:
/*
import { useGetCurrentAttemptQuery } from '../../redux/services/quiz'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
*/

export enum FocusableElement {
  ButtonLightningRound = 'button-lightning-round',
  ButtonPlayerPack = 'button-player-pack'
}

export const idUserDashboardContainer = 'user-dashboard-container'
export const idUserDashboardMainContent = 'user-dashboard-main-content'
export const idDashboardLevelTiles = 'dashboard-level-tiles'
export const idDashboardReskinBoard = 'dashboard-reskin-board'
export const idDashboardReskinHeaderColumn = 'dashboard-reskin-header-column'
export const idDashboardReskinMenuColumn = 'dashboard-reskin-menu-column'
export const idDashboardReskinStage = 'dashboard-reskin-stage'
export const DASHBOARD_RESKIN_SHELL_CLASS = 'font-primary flex h-[100dvh] flex-col overflow-hidden pt-4 pb-0'
export const DASHBOARD_RESKIN_HEADER_CLASS = 'relative z-10 shrink-0 overflow-visible pb-2'
export const DASHBOARD_RESKIN_MAIN_CLASS = 'relative min-h-0 flex-1 overflow-hidden px-4'
export { DASHBOARD_RESKIN_MENU_STAGE_CLASS, DASHBOARD_RESKIN_CHROME_MAX_CLASS }
export const DASHBOARD_RESKIN_STAGE_CLASS = 'absolute inset-0 flex items-center justify-center'
export const DASHBOARD_RESKIN_FOOTER_WRAP_CLASS = `relative z-20 mx-auto ${DASHBOARD_RESKIN_CHROME_MAX_CLASS} shrink-0 pl-4`
export const DASHBOARD_RESKIN_BOARD_CLASS = `flex w-full min-w-0 flex-col ${POINT_BAR_HORIZONTAL_INSET_CLASS}`
export const DASHBOARD_RESKIN_BOARD_LIFT_CLASS = '-translate-y-[105px]'
export const DASHBOARD_RESKIN_BOARD_STACK_CLASS = 'relative w-full min-w-0'
export const DASHBOARD_RESKIN_LEVEL_ROW_CLASS = 'relative z-10 flex w-full min-w-0 items-center justify-between gap-x-[2%] overflow-visible'
export const DASHBOARD_RESKIN_LIGHTNING_ROW_CLASS = 'absolute top-full left-0 right-0 z-0 flex w-full min-w-0 justify-center mt-[calc(2%-94/1034*(100%-min(calc(326px*0.8),(100%-6%)/4)))] pb-5'

const UserDashboard: React.FC = () => {
  const [experiencePoints, setExperiencePoints] = useState<number>(0)
  const navigate = useNavigate()
  const [filteredModules, setFilteredModules] = useState<Module[]>([])
  const [currentSession, setCurrentSession] = useState<any | null>(null)
  const { currentAttempt } = useSelector((state: RootState) => state.quiz)
  const { modules } = useSelector((state: RootState) => state.module)
  const { state } = useLocation()
  const { userProfile } = useAuth()
  const keyboardControls = useKeyboardControls()
  const dispatch = useDispatch()
  const DASHBOARD_INSTRUCTIONS_VIDEO = `${config.loadEnvironmentVariables().courseFilesUrl}video/dashboard-instructions.mp4`

  const bgMusic = useBackgroundMusic()
  const bgmTrackNormal = getBgmUrl(BgmTrack.Dashboard)
  const bgmTrackCompleted = getBgmUrl(BgmTrack.Completed)
  const sfxLevelEnterUrl = getSfxUrl(SfxTrack.LevelEnter)
  const userPreferences = useUserPreferences()

  /**
   * Will attempt to get the attempts and sessions for the user.
   * If the user has no attempts, it will create a new one.
   */
  const fetchAttemptsAndSessions = useCallback(async () => {
    const response = await apiServices.getCurrentAttemptAndSession()
    const currentAttempt = response?.data?.data?.attempt
    const currentSession = response?.data?.data?.session
    dispatch(setCurrentAttempt(currentAttempt))

    setExperiencePoints((currentAttempt?.totalScore != null) ? currentAttempt.totalScore : 0)

    // NOTE: sessions are confusing and do not make sense.  These will go away eventually in place of using
    // only attempts. Session data is meant to track user browser sessions and will be replaced with telemetry
    // tools.
    setCurrentSession(currentSession)
  }, [dispatch])

  const fetchModules = useCallback(async () => {
    if (!Array.isArray(modules) || modules.length <= 0) {
      const res = await apiServices.fetchModules()
      const modulesArr = res?.data?.data?.modules
      const m = modulesArr.filter(
        (module: Module) => `${module.name}`.toLowerCase() !== 'lightning').sort((a: any, b: any) => a.sort - b.sort)
      dispatch(setModules(m))
    }
  }, [dispatch, modules])

  useEffect(() => {
    if (modules !== null && modules !== undefined && modules.length > 0) {
      // get first active module
      const firstActiveModule = modules.find((module) => {
        return module.isActive
      })

      if (firstActiveModule !== undefined) {
        dispatch(setFirstActiveModule(firstActiveModule?.name))
      }

      const updatedModules = modules.map((module) => {
        if (module.id === currentSession?.moduleId) {
          return { ...module, isCurrent: true } // Create a new object
        }
        return { ...module, isCurrent: false } // Ensure all others are set to false
      })

      const currentModule = updatedModules.find((module: Module) => module.isCurrent)

      const finalModules = updatedModules.map((module) => {
        if (currentModule !== undefined) {
          // Find the positions in the sorted module array
          const currentModuleIndex = updatedModules.findIndex(m => m.id === currentModule.id)
          const moduleIndex = updatedModules.findIndex(m => m.id === module.id)

          if (moduleIndex < currentModuleIndex || !module.isActive) {
            module.isCompleted = true
            module.isLocked = false
            module.isCurrent = false
          } else if (moduleIndex > currentModuleIndex) {
            module.isCompleted = false
            module.isLocked = true
            module.isCurrent = false
          }
        } else {
          module.isCompleted = false
          module.isLocked = true
          module.isCurrent = false
        }

        if (currentSession?.name === 'Lightning') {
          module.isCompleted = true
          module.isLocked = false
          module.isCurrent = false
        }

        return module
      })
      setFilteredModules(finalModules)
    }
  }, [currentSession?.moduleId, currentSession?.name, dispatch, modules])

  /**
   * Attempts data required to load the page and set up the user to start taking courses.
   * TODO: refactor with Redux flow.
   */
  useEffect(() => {
    void fetchAttemptsAndSessions()
    void fetchModules()
  }, [fetchAttemptsAndSessions, fetchModules])

  // Manage BG music - wait for preferences to load before playing
  useEffect(() => {
    // Don't play music until preferences are loaded
    if (!userPreferences.isPreferencesLoaded) {
      return
    }

    let url: string
    if (currentAttempt?.status === 'completed') {
      url = bgmTrackCompleted
    } else {
      url = bgmTrackNormal
    }

    bgMusic.play(url)
    return () => {
      bgMusic.pause()
    }
  }, [bgMusic, bgmTrackNormal, bgmTrackCompleted, currentAttempt?.status, userPreferences.isPreferencesLoaded])

  useEffect(() => {
    const walkthroughCompleted = userProfile?.user_profile.walkthrough_completed
    const profileUpdated = state?.profileUpdated
    logger.log('Dashboard walkthrough check', { walkthrough_completed: walkthroughCompleted, profileUpdated })
    if (walkthroughCompleted !== true && profileUpdated !== true) {
      logger.log('Redirecting to walkthrough video')
      navigate('/user-dashboard/instructions', { state: { videoPath: DASHBOARD_INSTRUCTIONS_VIDEO, returnPath: '/user-dashboard' } })
    }
  }, [DASHBOARD_INSTRUCTIONS_VIDEO, userProfile, navigate, state?.profileUpdated])

  useEffect(() => {
    if (userProfile?.user_profile.accepted_terms !== true) {
      navigate('/terms-of-use')
    }
  }, [navigate, userProfile?.user_profile])

  const handleCourseClick = useCallback(async (module: Module) => {
    bgMusic.playSoundFx(sfxLevelEnterUrl)

    if (currentSession?.status === 'pending') {
      await apiServices.startNextSession(currentSession?.attemptId)
    }

    if (currentAttempt?.status === 'completed' || module.isCompleted) {
      navigate(`/user-dashboard/module-replay?module=${String(module.name)}`)
    } else {
      navigate(`/user-dashboard/courses/${String(currentSession.id)}`)
    }
  }, [currentSession, currentAttempt?.status, navigate, bgMusic, sfxLevelEnterUrl])

  const currentModuleIndex = filteredModules.findIndex((item) => item.isCurrent)

  const levelTiles = (
    <div
      id={idDashboardLevelTiles}
      data-testid={idDashboardLevelTiles}
      className={DASHBOARD_RESKIN_LEVEL_ROW_CLASS}
      role='region'
    >
      {
        filteredModules.map((module: Module, index: number) => (
          <CourseTile
            key={`Level ${index + 1}: ${module.name}`}
            loading={modules === null || modules === undefined || currentSession === null || currentSession === undefined}
            label={`Level ${index + 1}: ${module.name}`}
            module={module}
            onClick={() => { void handleCourseClick(module) }}
            reviewState={currentAttempt?.status === 'completed'} // If the user has completed their attempt, all courses should be in the review state.
            lockedIteration={getDashboardReskinLockedIteration(currentModuleIndex)}
          />
        ))
      }
    </div>
  )

  const lightningRound = currentSession?.name === 'Lightning' && currentAttempt?.status !== 'completed'
    ? (
      <div
        className={DASHBOARD_RESKIN_LIGHTNING_ROW_CLASS}
        role='region'
      >
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonLightningRound)}
          highlightEffect={HighlightEffect.ButtonHexOutline}
          data-testid={`highlight-${FocusableElement.ButtonLightningRound}`}
          className={`${DASHBOARD_RESKIN_LIGHTNING_WIDTH_CLASS} min-w-0`}
        >
          <KeyboardControl>
            <button
              id={FocusableElement.ButtonLightningRound}
              data-testid={FocusableElement.ButtonLightningRound}
              className='block w-full min-w-0 cursor-pointer bg-transparent p-0'
              onClick={() => { void handleCourseClick(currentSession) }}
              aria-label='Launch Level: Lightning Round'
            >
              <img
                src={DASHBOARD_RESKIN_LIGHTNING_LAUNCH_SRC}
                alt=''
                className={DASHBOARD_RESKIN_LIGHTNING_SIZE_CLASS}
                data-testid='dashboard-reskin-lightning'
              />
            </button>
          </KeyboardControl>
        </ControlHighlight>
      </div>
      )
    : null

  return (
    <UserDashboardContainer
      currentAttempt={currentAttempt}
      experiencePoints={experiencePoints}
      hasLightningRound={lightningRound != null}
    >
      <div
        id={idDashboardReskinBoard}
        data-testid={idDashboardReskinBoard}
        className={DASHBOARD_RESKIN_BOARD_CLASS}
      >
        <div className={DASHBOARD_RESKIN_BOARD_STACK_CLASS}>
          {levelTiles}
          {lightningRound}
        </div>
      </div>
    </UserDashboardContainer>
  )
}

interface PlayerPackButtonProps {
  buttonClassName: string
  className: string
  isFocused: boolean
  onClick: () => void
}

const PlayerPackButton: React.FC<PlayerPackButtonProps> = ({
  buttonClassName,
  className,
  isFocused,
  onClick
}) => (
  <div
    id='player-pack-button-container'
    className={className}
  >
    <ControlHighlight
      active={isFocused}
      highlightEffect={HighlightEffect.ButtonHexOutline}
      data-testid={`highlight-${FocusableElement.ButtonPlayerPack}`}
    >
      <KeyboardControl>
        <button
          id={FocusableElement.ButtonPlayerPack}
          data-testid={FocusableElement.ButtonPlayerPack}
          className={buttonClassName}
          onClick={onClick}
        >
          <img
            alt='Open Player Pack'
            src={DASHBOARD_RESKIN_MENU_ICON_SRC}
            className={DASHBOARD_RESKIN_MENU_ICON_SIZE_CLASS}
          />
        </button>
      </KeyboardControl>
    </ControlHighlight>
  </div>
)

interface UserDashboardContainerProps {
  children: React.ReactNode
  currentAttempt?: Attempt | null
  experiencePoints?: number
  hasLightningRound?: boolean
}

const UserDashboardContainer: React.FC<UserDashboardContainerProps> = ({
  children,
  currentAttempt = null,
  experiencePoints,
  hasLightningRound = false
}) => {
  const [pageTitle] = useState<string>(defaultPageTitle)
  const navigate = useNavigate()
  const keyboardControls = useKeyboardControls()
  const fitScale = useDashboardReskinFitScale()

  return (
    <div
      id={idUserDashboardContainer}
      data-testid={idUserDashboardContainer}
      className={DASHBOARD_RESKIN_SHELL_CLASS}
      style={getDashboardContainerBackgroundStyle()}
    >
      <RemoveScrollBar />
      <SkipLink targetId={idUserDashboardMainContent} />
      <header className={DASHBOARD_RESKIN_HEADER_CLASS}>
        <ReskinStudentHeader
          columnId={idDashboardReskinHeaderColumn}
          columnTestId={idDashboardReskinHeaderColumn}
          menuColumnId={idDashboardReskinMenuColumn}
          menuColumnTestId={idDashboardReskinMenuColumn}
          columnStyle={fitScale.style}
        >
          <PointBar isCompleted={currentAttempt?.status === 'completed'} totalExperiencePoints={experiencePoints} />
        </ReskinStudentHeader>
      </header>
      <main
        id={idUserDashboardMainContent}
        data-testid={idUserDashboardMainContent}
        tabIndex={-1}
        className={DASHBOARD_RESKIN_MAIN_CLASS}
      >
        <PageTitle title={pageTitle} />
        <div
          id={idDashboardReskinStage}
          data-testid={idDashboardReskinStage}
          ref={fitScale.containerRef}
          className={DASHBOARD_RESKIN_STAGE_CLASS}
        >
          <div
            ref={fitScale.contentRef}
            className={hasLightningRound
              ? `${DASHBOARD_RESKIN_CHROME_MAX_CLASS} ${DASHBOARD_RESKIN_BOARD_LIFT_CLASS}`
              : DASHBOARD_RESKIN_CHROME_MAX_CLASS}
            style={fitScale.style}
          >
            {children}
          </div>
        </div>
      </main>
      <div className={DASHBOARD_RESKIN_FOOTER_WRAP_CLASS}>
        <Footer>
          <PlayerPackButton
            className='flex'
            buttonClassName='flex cursor-pointer items-start rounded text-dopage-lightGreen font-bold'
            onClick={() => navigate('setting')}
            isFocused={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonPlayerPack)}
          />
        </Footer>
      </div>
    </div>
  )
}

export default UserDashboard
